/**
 * CancelOrderModal Component
 * Modal for cancelling an entire sale order with inventory return
 * WCAG 2.1 AA compliant
 */

import React, { useState } from 'react';
import { Modal } from '@/components/molecules';
import { Button, Icon, Badge } from '@/components/atoms';
import { useCancelSaleMutation } from '@/hooks/useErpApi';
import { CANCELLATION_REASONS, type CancellationReasonValue } from '@/schemas/cancellations';
import type { SaleResponse } from '@kisanlink/erp-service';

type ModalStep = 'review' | 'confirm' | 'processing' | 'success' | 'error';

export interface CancelOrderModalProps {
  sale: SaleResponse;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  sale,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<ModalStep>('review');
  const [reasonCategory, setReasonCategory] = useState<CancellationReasonValue | ''>('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutateAsync: cancelSale } = useCancelSaleMutation();

  const handleClose = () => {
    if (step === 'processing') return; // Prevent closing during API call
    setStep('review');
    setReasonCategory('');
    setAdditionalNotes('');
    setErrorMessage(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!reasonCategory) return;
    setStep('confirm');
  };

  const handleCancel = async () => {
    setStep('processing');
    setErrorMessage(null);

    try {
      const reason = additionalNotes ? `${reasonCategory}: ${additionalNotes}` : reasonCategory;

      await cancelSale({
        id: sale.id,
        reason,
      });

      setStep('success');
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 1500);
    } catch (error) {
      setStep('error');
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to cancel order. Please try again.'
      );
    }
  };

  const handleRetry = () => {
    setStep('review');
    setErrorMessage(null);
  };

  const renderReviewStep = () => (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-error-50 border border-error-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="TriangleAlert" size={24} className="text-error-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-error-900">This action cannot be undone</h3>
            <p className="text-sm text-error-700 mt-1">
              Cancelling this order will return inventory to original batches, reverse any discounts
              applied, and void tax records.
            </p>
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div className="bg-neutral-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-neutral-900 mb-3">Order Summary</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-neutral-600">Order ID:</span>
            <span className="ml-2 font-medium text-neutral-900 font-mono">
              SO-{sale.id.substring(0, 8)}
            </span>
          </div>
          <div>
            <span className="text-neutral-600">Status:</span>
            <span className="ml-2">
              <Badge variant="warning">{sale.status}</Badge>
            </span>
          </div>
          <div>
            <span className="text-neutral-600">Items:</span>
            <span className="ml-2 font-medium text-neutral-900">{sale.items?.length || 0}</span>
          </div>
          <div>
            <span className="text-neutral-600">Total Amount:</span>
            <span className="ml-2 font-medium text-neutral-900">
              ₹{sale.total_amount.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Items to Cancel */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-900 mb-3">Items to be Cancelled</h4>
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-error-50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-error-900">Batch ID</th>
                <th className="px-4 py-2 text-right font-semibold text-error-900">Qty</th>
                <th className="px-4 py-2 text-right font-semibold text-error-900">Unit Price</th>
                <th className="px-4 py-2 text-right font-semibold text-error-900">Line Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {sale.items && sale.items.length > 0 ? (
                sale.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-2 text-neutral-900 font-mono text-xs">
                      {item.batch_id?.substring(0, 12) || 'N/A'}
                    </td>
                    <td className="px-4 py-2 text-right text-neutral-900">{item.quantity}</td>
                    <td className="px-4 py-2 text-right text-neutral-900">
                      ₹{item.selling_price.toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-right font-medium text-neutral-900">
                      ₹{item.line_total.toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-4 py-4 text-center text-sm text-neutral-500">
                    No items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Refund Summary */}
      <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-warning-900 mb-3">Refund Impact</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex justify-between">
            <span className="text-warning-700">Subtotal:</span>
            <span className="font-medium text-warning-900">
              ₹{sale.breakdown?.base_amount?.toFixed(2) || sale.total_amount.toFixed(2)}
            </span>
          </div>
          {sale.breakdown?.tax_amount > 0 && (
            <div className="flex justify-between">
              <span className="text-warning-700">Tax Reversal:</span>
              <span className="font-medium text-warning-900">
                ₹{sale.breakdown.tax_amount.toFixed(2)}
              </span>
            </div>
          )}
          {sale.breakdown?.discount_amount > 0 && (
            <div className="flex justify-between">
              <span className="text-warning-700">Discount Restored:</span>
              <span className="font-medium text-success-700">
                +₹{sale.breakdown.discount_amount.toFixed(2)}
              </span>
            </div>
          )}
          <div className="col-span-2 border-t border-warning-200 pt-2 mt-2 flex justify-between">
            <span className="font-semibold text-warning-900">Total Refund:</span>
            <span className="font-bold text-warning-900">₹{sale.total_amount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Reason Selection */}
      <div>
        <label htmlFor="cancel-reason" className="block text-sm font-medium text-neutral-700 mb-2">
          Cancellation Reason <span className="text-error-500">*</span>
        </label>
        <select
          id="cancel-reason"
          value={reasonCategory}
          onChange={(e) => setReasonCategory(e.target.value as CancellationReasonValue)}
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-error-500 focus:border-error-500"
          required
          aria-required="true"
        >
          <option value="">Select a reason...</option>
          {CANCELLATION_REASONS.map((reason) => (
            <option key={reason.value} value={reason.value}>
              {reason.label}
            </option>
          ))}
        </select>
      </div>

      {/* Additional Notes */}
      <div>
        <label htmlFor="cancel-notes" className="block text-sm font-medium text-neutral-700 mb-2">
          Additional Notes (Optional)
        </label>
        <textarea
          id="cancel-notes"
          value={additionalNotes}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          placeholder="Provide any additional details..."
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-error-500 focus:border-error-500 resize-none"
          rows={3}
          maxLength={500}
        />
        <p className="text-xs text-neutral-500 mt-1">{additionalNotes.length}/500 characters</p>
      </div>
    </div>
  );

  const renderConfirmStep = () => (
    <div className="space-y-6">
      <div className="text-center py-8">
        <div className="bg-error-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <Icon name="TriangleAlert" size={32} className="text-error-600" />
        </div>
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">Confirm Cancellation</h3>
        <p className="text-neutral-600 mb-4">
          Are you sure you want to cancel order{' '}
          <span className="font-mono font-medium">SO-{sale.id.substring(0, 8)}</span>?
        </p>
        <p className="text-sm text-error-600 font-medium">
          This will return ₹{sale.total_amount.toFixed(2)} worth of inventory.
        </p>
      </div>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="text-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-error-600 mx-auto mb-4" />
      <p className="text-neutral-600" aria-live="polite">
        Cancelling order and returning inventory...
      </p>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center py-12">
      <div className="bg-success-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
        <Icon name="Check" size={32} className="text-success-600" />
      </div>
      <h3 className="text-lg font-semibold text-success-900 mb-2">Order Cancelled</h3>
      <p className="text-neutral-600" aria-live="polite">
        Inventory has been returned to original batches.
      </p>
    </div>
  );

  const renderErrorStep = () => (
    <div className="space-y-6">
      <div className="text-center py-8">
        <div className="bg-error-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <Icon name="X" size={32} className="text-error-600" />
        </div>
        <h3 className="text-lg font-semibold text-error-900 mb-2">Cancellation Failed</h3>
        <p className="text-neutral-600 mb-4" aria-live="assertive">
          {errorMessage || 'An error occurred while cancelling the order.'}
        </p>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (step) {
      case 'review':
        return renderReviewStep();
      case 'confirm':
        return renderConfirmStep();
      case 'processing':
        return renderProcessingStep();
      case 'success':
        return renderSuccessStep();
      case 'error':
        return renderErrorStep();
    }
  };

  const renderFooter = () => {
    switch (step) {
      case 'review':
        return (
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={handleClose}>
              Keep Order
            </Button>
            <Button variant="error" onClick={handleConfirm} disabled={!reasonCategory}>
              <Icon name="X" size={16} className="mr-2" />
              Cancel Order
            </Button>
          </div>
        );
      case 'confirm':
        return (
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setStep('review')}>
              Go Back
            </Button>
            <Button variant="error" onClick={handleCancel}>
              <Icon name="TriangleAlert" size={16} className="mr-2" />
              Yes, Cancel Order
            </Button>
          </div>
        );
      case 'processing':
        return null;
      case 'success':
        return null;
      case 'error':
        return (
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={handleClose}>
              Close
            </Button>
            <Button variant="primary" onClick={handleRetry}>
              Try Again
            </Button>
          </div>
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'success' ? 'Cancellation Complete' : 'Cancel Order'}
      size="lg"
      closeOnBackdropClick={step !== 'processing'}
      closeOnEscape={step !== 'processing'}
      footer={renderFooter()}
    >
      {renderContent()}
    </Modal>
  );
};
